/**
 * UserPublicProfilePage
 *
 * Displays another user's public profile with connection status and actions.
 * Accessed via /users/:userId or /users/username/:username
 *
 * Features:
 * - User avatar, name, username, bio
 * - Connection status and connect/disconnect buttons
 * - Mutual connections display
 * - Redirects to own profile if viewing self
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User,
  Users,
  Calendar,
  Loader2,
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  MessageSquare,
  Send,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ConnectionStatusButton } from '@/components/connections/ConnectionStatusButton';
import { MutualConnectionsModal } from '@/components/connections/MutualConnectionsModal';
import { connectionsApi } from '@/services/api/connections';
import { useAuthStore } from '@/stores/auth/auth-store';
import { FOREST_GREEN } from '@/styles/tokens';
import { useConversations, useMessagingActions } from '@/stores/messaging';
import type { UserPublicProfile, MutualConnection } from '@/types/connections';

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Format join date
 */
const formatJoinDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

/**
 * MutualConnectionsSection - Shows mutual connections with "View all" option
 */
interface MutualConnectionsSectionProps {
  userId: string;
  userName: string;
  count: number;
  connections?: MutualConnection[];
}

const MutualConnectionsSection: React.FC<MutualConnectionsSectionProps> = ({
  userId,
  userName,
  count,
  connections = [],
}) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  if (count === 0) {
    return null;
  }

  // Show first 5 connections, with "View all" button if more than 5
  const displayConnections = connections.slice(0, 5);
  const showViewAll = count > 5;

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Users className="h-4 w-4" style={{ color: FOREST_GREEN.DEFAULT }} />
          {count} Mutual Connection{count !== 1 ? 's' : ''}
        </h3>
        {showViewAll && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              console.log('[MutualConnections] Opening modal for user:', userId);
              setShowModal(true);
            }}
            className="text-xs h-7 px-2"
          >
            View all
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        )}
      </div>

      {/* Clickable avatars */}
      <div className="flex flex-wrap gap-2">
        {displayConnections.map((conn) => (
          <button
            key={conn.id}
            onClick={() => {
              console.log('[MutualConnections] Navigating to user:', conn.username);
              navigate(`/user/${conn.username}`);
            }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 transition-colors group"
            title={`${conn.firstName} ${conn.lastName} (@${conn.username})`}
          >
            <Avatar className="h-6 w-6 ring-2 ring-transparent group-hover:ring-primary/20 transition-all">
              <AvatarImage src={conn.avatar} alt={conn.username} />
              <AvatarFallback
                className="text-xs"
                style={{
                  backgroundColor: FOREST_GREEN.faint,
                  color: FOREST_GREEN.DEFAULT,
                }}
              >
                {getInitials(conn.firstName, conn.lastName, conn.username)}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm">
              {conn.firstName} {conn.lastName}
            </span>
          </button>
        ))}
        {showViewAll && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 transition-colors text-sm text-muted-foreground"
          >
            +{count - 5} more
          </button>
        )}
      </div>

      {/* Modal for viewing all mutual connections */}
      <MutualConnectionsModal
        open={showModal}
        onOpenChange={setShowModal}
        userId={userId}
        userName={userName}
      />
    </div>
  );
};

/**
 * ProfileSkeleton - Loading skeleton for the profile
 */
const ProfileSkeleton: React.FC = () => (
  <Card className="max-w-2xl mx-auto">
    <CardHeader className="flex flex-row items-start gap-6 pb-4">
      <Skeleton className="h-24 w-24 rounded-full" />
      <div className="flex-1 space-y-3">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-10 w-32" />
    </CardContent>
  </Card>
);

/**
 * UserPublicProfilePage - Main component
 */
export const UserPublicProfilePage: React.FC = () => {
  const { userId, username } = useParams<{ userId?: string; username?: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();

  const [profile, setProfile] = useState<UserPublicProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStartingConversation, setIsStartingConversation] = useState(false);

  // Messaging hooks
  const conversations = useConversations();
  const { startConversation, fetchConversations } = useMessagingActions();

  // Fetch profile
  const fetchProfile = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[UserPublicProfile] Fetching profile for:', userId || username);

      let response;
      if (userId) {
        response = await connectionsApi.getUserProfile(userId);
      } else if (username) {
        response = await connectionsApi.getUserProfileByUsername(username);
      } else {
        throw new Error('No user identifier provided');
      }

      if (response.success && response.data) {
        // Redirect to own profile page if viewing self
        if (response.data.connectionStatus === 'self') {
          console.log('[UserPublicProfile] Viewing own profile, redirecting to /profile');
          navigate('/profile', { replace: true });
          return;
        }

        setProfile(response.data);
        console.log('[UserPublicProfile] Profile loaded:', response.data.username);
      } else {
        throw new Error(response.error || 'Failed to load profile');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load profile';
      console.error('[UserPublicProfile] Error loading profile:', message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (userId || username) {
      fetchProfile();
    }
  }, [userId, username]);

  // Handle status change (refetch profile)
  const handleStatusChange = () => {
    console.log('[UserPublicProfile] Connection status changed, refetching profile');
    fetchProfile();
  };

  // Handle message button click
  const handleMessageClick = async () => {
    if (!profile) return;

    console.log('[UserPublicProfile] Message button clicked for user:', profile.id);

    // Check if there's an existing conversation with this user
    const existingConversation = conversations.find(
      (c) => c.participant.id === profile.id
    );

    if (existingConversation) {
      console.log('[UserPublicProfile] Found existing conversation:', existingConversation.id);
      navigate(`/dashboard/messages?conversation=${existingConversation.id}`);
      return;
    }

    // No existing conversation - navigate to messages with userId to start new conversation
    console.log('[UserPublicProfile] No existing conversation, navigating to messages with userId');
    navigate(`/dashboard/messages?newMessage=${profile.id}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container py-8">
        <ProfileSkeleton />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container py-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-semibold mb-2">Unable to Load Profile</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go Back
                </Button>
                <Button onClick={fetchProfile}>Try Again</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No profile
  if (!profile) {
    return (
      <div className="container py-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12">
            <div className="text-center">
              <User className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">User Not Found</h2>
              <p className="text-muted-foreground mb-6">
                The user you're looking for doesn't exist or their profile is private.
              </p>
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const displayName = profile.firstName && profile.lastName
    ? `${profile.firstName} ${profile.lastName}`
    : profile.username;

  const initials = getInitials(profile.firstName, profile.lastName, profile.username);

  return (
    <div className="container py-8">
      <Card className="max-w-2xl mx-auto">
        <CardHeader className="pb-4">
          {/* Back button */}
          <Button
            variant="ghost"
            size="sm"
            className="w-fit mb-4 -ml-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {/* Profile header */}
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Avatar */}
            <Avatar className="h-24 w-24 flex-shrink-0">
              <AvatarImage src={profile.avatar} alt={displayName} />
              <AvatarFallback
                className="text-2xl font-semibold"
                style={{
                  backgroundColor: FOREST_GREEN.faint,
                  color: FOREST_GREEN.DEFAULT,
                }}
              >
                {initials}
              </AvatarFallback>
            </Avatar>

            {/* User info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold truncate">{displayName}</h1>
              <p className="text-muted-foreground">@{profile.username}</p>

              {/* Join date */}
              <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Joined {formatJoinDate(profile.createdAt)}</span>
              </div>

              {/* Connection status button and Message button */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <ConnectionStatusButton
                  userId={profile.id}
                  connectionId={profile.connectionId}
                  status={profile.connectionStatus}
                  displayName={displayName}
                  onStatusChange={handleStatusChange}
                />
                {/* Message button - only show if connected */}
                {profile.connectionStatus === 'connected' && (
                  <Button
                    variant="outline"
                    onClick={handleMessageClick}
                    className="gap-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Message
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Bio */}
          {profile.bio && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-2">About</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {profile.bio}
              </p>
            </div>
          )}

          {/* Mutual connections */}
          <MutualConnectionsSection
            userId={profile.id}
            userName={displayName}
            count={profile.mutualConnectionsCount}
            connections={profile.mutualConnections}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default UserPublicProfilePage;
